import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Chip, Collapse, keyframes, Stack, Typography } from '@mui/material';
import { type ReactNode, useState } from 'react';

const borderColorHighlight = keyframes`
    0%, 100% {
        border-color: #e8eaed;
    }
    50% {
        border-color: #3678D7;
    }
`;

interface Props {
    stepNumber: string;
    title: string;
    description: string;
    defaultExpanded?: boolean;
    isComplete?: boolean;
    showStatus?: boolean;
    isNew?: boolean;
    highlighted?: boolean;
    children: ReactNode;
}

const headerBackground = {
    default: 'linear-gradient(135deg, #e3f2fd 0%, #f1f8ff 100%)',
    hover: 'linear-gradient(135deg, #d1e7fd 0%, #e8f4ff 100%)',
    completed: 'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%)',
};

export default function NewProspectStep({
    stepNumber,
    title,
    description,
    defaultExpanded = true,
    isComplete = false,
    showStatus = true,
    isNew = false,
    highlighted = false,
    children,
}: Props) {
    const [isHovered, setIsHovered] = useState(false);
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    return (
        <Box
            sx={{
                border: '1px solid #e8eaed',
                borderRadius: '6px',
                animation: highlighted
                    ? `${borderColorHighlight} 1.6s ease-in-out infinite`
                    : undefined,
            }}
        >
            <Box sx={{ overflow: 'hidden', borderRadius: '5px' }}>
                <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{
                        p: '14px 16px',
                        borderBottom: '1px solid #e8eaed',
                        cursor: 'pointer',
                        transition: 'background 0.3s ease',
                        background: isComplete
                            ? headerBackground.completed
                            : isHovered
                              ? headerBackground.hover
                              : headerBackground.default,
                    }}
                    onClick={() => setIsExpanded((prev) => !prev)}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 28,
                            height: 28,
                            borderRadius: '100%',
                            backgroundColor: isComplete ? '#4caf50' : '#1976d2',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '16px',
                            lineHeight: 1,
                        }}
                    >
                        {isComplete ? '✓' : stepNumber}
                    </div>
                    <Stack sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600} color="#202124">
                            {title}
                        </Typography>
                        <Typography variant="caption" color="#5f6368" sx={{ mt: '2px' }}>
                            {description}
                        </Typography>
                    </Stack>
                    {showStatus && (
                        <Chip
                            label={isComplete ? 'Completed' : 'Pending'}
                            size="small"
                            sx={{
                                background: isComplete
                                    ? 'rgba(76, 175, 80, 0.1)'
                                    : 'rgba(255, 255, 255, 0.7)',
                                color: isComplete ? '#2e7d32' : '#5f6368',
                                fontWeight: 500,
                            }}
                        />
                    )}
                    {isNew && (
                        <Chip
                            label="New!"
                            size="small"
                            sx={{
                                background: 'rgba(76, 175, 80, 0.1)',
                                color: '#2e7d32',
                                fontWeight: 500,
                            }}
                        />
                    )}
                    <ExpandMoreIcon
                        sx={{
                            color: '#5f6368',
                            transition: 'transform 0.3s ease',
                            transform: isExpanded ? 'rotate(180deg)' : undefined,
                        }}
                    />
                </Stack>
                <Collapse in={isExpanded} unmountOnExit={false}>
                    <Stack sx={{ p: 2, background: '#fafbfc' }}>{children}</Stack>
                </Collapse>
            </Box>
        </Box>
    );
}
